import { jsx as _jsx } from "react/jsx-runtime";
// Auth context — manages JWT tokens, user profile, and session lifecycle.
//
// Moved here from cloud/dp/shell/src/auth/auth-context.tsx so the
// platform shell, dev-shell fixtures, and any other app can mount the
// same provider and read the same `useAuth()` value. The shell now
// imports `AuthProvider` and `useAuth` back from @zion/sdk-react,
// which collapses the two-React-instance saga to a single canonical
// AuthContext object.
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, } from 'react';
import { sessionResponseSchema } from '@zion/sdk-contracts';
import { PermissionProvider } from '../permissions/PermissionContext.js';
import { isTokenExpired, extractClaims } from './token-utils.js';
import { sessionStore } from './session-store.js';
import { adoptSession, getAccessToken, clearTokens, hydrateTokens, refreshAccessToken, startRefreshRetry, startSilentRefresh, stopSilentRefresh, startSessionSync, stopSessionSync, startVisibilityRefresh, stopVisibilityRefresh, setRefreshCallbacks, withRefreshLock, revokeServerSession, TerminalAuthError, TransientRefreshError, } from './token-manager.js';
import { REQUEST_HEADER_TOO_LARGE_STATUS } from './token-manager-utils.js';
// The logout bounds moved to `token-manager.ts` with `revokeServerSession`; this module keeps exporting them.
export { LOGOUT_ATTEMPTS, LOGOUT_REQUEST_TIMEOUT_MS, LOGOUT_RETRY_DELAY_MS } from './token-manager.js';
const AuthContext = createContext(null);
/**
 * The scope of a session end. A token string ends only that token's session. `null`
 * ends only the absence of one (local state, while nothing is stored). `ANY_SESSION`
 * ends whatever is stored and is reserved for the user's own `logout()`; every
 * verdict-driven end is scoped (#2680).
 */
const ANY_SESSION = Symbol('any-session');
/**
 * `AuthProvider.login` was handed a body that is not a `SessionResponse` (R-37).
 *
 * ⛔ A FIXED SENTENCE, AND THE `ZodError` IS THE `cause`. Six shell pages render a caught error's
 * `message` into a `role="alert"` region, and zod 4 puts a 754-character JSON array of issues in
 * `ZodError.message`. An assertive live region then reads that JSON aloud. The sentence below is
 * what those pages render; the issues stay reachable at `error.cause` for a console or a log.
 *
 * It is a programming error in the caller, not a session verdict: the caller passed a body it did
 * not narrow. `RecoveryLoginPage` strips `redirectTo` before it calls `login` for this reason
 * (R-21).
 */
export class SessionResponseError extends Error {
    /** @param cause the `ZodError` `sessionResponseSchema.parse` threw. */
    constructor(cause) {
        super('The sign-in response did not match the session contract', { cause });
        this.name = 'SessionResponseError';
    }
}
/** Thrown by `fetchMe` so the hydrate/login flows can classify the failure:
 *  401/403 means the token is genuinely rejected (⇒ logout); 431 means the
 *  request could not be delivered and never will be (⇒ keep the session, name
 *  the reason); any other status or a network error is transient (⇒ keep the
 *  session and let the next refresh retry). */
export class AuthFetchMeError extends Error {
    status;
    constructor(status) {
        super(`fetchMe failed: ${status}`);
        this.status = status;
        this.name = 'AuthFetchMeError';
    }
}
/**
 * The token itself was rejected ⇒ end the session.
 *
 * ⛔ ONE DEFINITION FOR ALL THREE `/me` CALL SITES (`login`, the hydrate
 * effect, `recoverIdentity`). The classification was hand-copied three times
 * and each copy's docblock promised it would stay identical to the other two.
 * A promise in a comment is not a mechanism; this function is.
 */
function isRejectedSession(err) {
    return err instanceof AuthFetchMeError && (err.status === 401 || err.status === 403);
}
/**
 * The request was refused for its size ⇒ keep the session and name the reason.
 * See {@link AuthState.sessionTooLarge}.
 */
function isOversizedRequest(err) {
    return (err instanceof AuthFetchMeError && err.status === REQUEST_HEADER_TOO_LARGE_STATUS);
}
/**
 * Upper bound on the profile request. `recoverIdentity` holds the token in
 * `recoveriesInFlightRef` until `fetchMe` settles, so a request that stalls
 * without rejecting would leave that marker in place and every later refresh
 * would return early: the identity would never recover, even once the backend
 * was healthy (Copilot, PR #2625). An abort throws, the catch classifies it
 * `AuthFetchMeError('network')`, which is transient and keeps the session, and
 * the `finally` clears the marker so the next refresh retries.
 */
export const ME_REQUEST_TIMEOUT_MS = 15_000;
async function fetchMe(token) {
    let res;
    try {
        res = await fetch('/v1/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
            signal: AbortSignal.timeout(ME_REQUEST_TIMEOUT_MS),
        });
    }
    catch {
        throw new AuthFetchMeError('network');
    }
    if (!res.ok)
        throw new AuthFetchMeError(res.status);
    return res.json();
}
/**
 * How long `loading` holds while the boot refresh's one retry runs (R-38, amending R-30).
 *
 * A transient boot failure is no verdict on the session, so the provider keeps the spinner up and
 * lets `startRefreshRetry`'s 5-second retry answer. A spinner with no bound is the other wrong
 * state, so the hold ends here whatever that retry is doing.
 *
 * ⛔ THE BOUND COVERS ONE RETRY, AND THE DERIVATION IS 5 PLUS 10. Five seconds is
 * `INITIAL_BACKOFF_SEC`, the delay before the retry leaves; ten is the allowance for its round
 * trip. The second retry is armed inside `runScheduledRefresh`'s catch, after the first has
 * settled, so it is always due after this bound and its verdict later still. Counting it here was
 * finding CD2-3.
 *
 * When the bound fires the provider settles signed out AND clears the pending retry, so no token
 * can land behind a login page: `ProtectedRoute` navigates with `replace`, and `LoginPage` never
 * reads `token`, so a late success would leave a signed-in session under a sign-in form (R-38).
 *
 * ⛔ CLEARING THE TIMER IS HALF THE GUARD. `REFRESH_REQUEST_TIMEOUT_MS` is 15 seconds, the same
 * as this bound, so a retry that left at 5 seconds can still be open when the bound fires:
 * `stopSilentRefresh()` cancels a pending timer and cannot recall a request in flight. The other
 * half is the retry generation in `token-manager.ts`, which `stopSilentRefresh` advances, so a
 * verdict that arrives after the hold is discarded rather than adopted (R-43).
 */
export const BOOT_RETRY_GRACE_MS = 15_000;
/**
 * A bearer for the logout request when the tab holds no usable one, or `null` to post nothing.
 *
 * ⛔ WITHOUT THIS THE CREDENTIAL OUTLIVES THE LOGOUT (R-41, R-49). The old guard skipped
 * `revokeServerSession` whenever the in-memory access token was absent or expired. The credential is an
 * HttpOnly cookie now, and script cannot delete it, so that skip left a live 30-day family
 * behind a logged-out browser and the next page load hydrated a session. R-41 kept the skip for
 * the body arm, where `clearTokens` drops the token as the pre-cookie client did. R-49 removed
 * that too: dropping the token ends this browser's access and leaves the family live on the
 * server for its own 30-day sliding expiry, which is the defect R-33 names.
 *
 * ⛔ CALLED BEFORE `endSession` TAKES THE REFRESH LOCK. `refreshAccessToken` runs `doRefresh`
 * under `withRefreshLock`, so minting inside that lock would wait on a lock the same call already
 * holds — a deadlock under the Web Locks API and under the in-tab queue alike.
 *
 * @param scope the session end's scope. Only the user's own `logout()` mints, and the five
 *   scoped sites reach the same outcome two different ways (CS3-2). The two refresh-verdict
 *   sites follow a 401 core-api answered on `/v1/auth/refresh`, and D7 has it clear both cookies
 *   before it replies, so nothing is left to revoke. The three `/v1/auth/me` sites follow a
 *   401 or 403 on a route that clears no cookie at all; each holds a token minted or refreshed
 *   moments earlier, so `endSession` takes that live bearer and posts the revoke itself, and the
 *   revoke is what clears both cookies. The `null` scope means no session is held at all.
 * @returns the fresh access token, or `null` when the tab must post no logout: the held token is
 *   already live and `endSession` will use it, or the refresh did not come back. A tab on the
 *   body arm mints here too (R-49). `doRefresh` sends its legacy token, and what comes back
 *   decides the revoke: a current pod's cookie session clears that key and the revoke goes out
 *   as `{}`, and an N-1 pod's token pair is written back and carried in the body (R-10, R-33).
 *
 *   ⛔ A REFUSAL DOES NOT ALWAYS CLEAR THE COOKIES (R-41 as amended, CS3-1). Two of the four
 *   cookie-arm 401s do: the token-rejected refusal and the no-credential refusal both run
 *   `clearRefreshCookie` and `clearAccessCookie`. An Origin-rejected 401 and an ambiguous-cookie
 *   401 clear nothing, and D17 makes all four bodies byte-identical, so the `catch` below cannot
 *   tell them apart. A refusal of either second kind therefore leaves the family live after the
 *   user clicked logout, with the server's own sliding expiry as the only control;
 *   `security-audit.md` records it as a residual beside the transient one. A transient failure
 *   leaves the cookie until its next use or its own expiry, which is the other residual.
 */
async function mintLogoutBearer(scope) {
    if (scope !== ANY_SESSION)
        return null;
    const held = getAccessToken();
    if (held !== null && !isTokenExpired(sessionStore.getExpiresAt()))
        return null;
    try {
        return await refreshAccessToken();
    }
    catch {
        return null;
    }
}
export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => getAccessToken());
    const [user, setUser] = useState(null);
    // ⛔ UNCONDITIONALLY `true`. A cold boot has no access token by construction now: the store is
    // module memory and a reload starts it empty (D9, R-7c). The pre-cookie version seeded this
    // from the stored token, so with the new store every reload would render signed out and the
    // mount effect's `if (!token) return` guard would skip hydration entirely.
    const [loading, setLoading] = useState(true);
    /**
     * ⛔ MIRRORS `user` SO THE REFRESH CALLBACK CAN READ IT WITHOUT CAPTURING IT.
     *
     * `recoverIdentity` below is a `useCallback` with stable dependencies, so it
     * is created ONCE and closes over the FIRST render's `user`, which is always
     * `null`. Reading `user` directly there would therefore see `null` forever
     * and refetch `/me` on EVERY refresh, for every session, silently converting
     * a recovery path into a per-interval request. The ref is what keeps the
     * refetch conditional.
     *
     * Written SYNCHRONOUSLY with the state transition, through `applyUser`, the
     * single writer of `user`. An earlier version mirrored it in a passive
     * effect, which flushes after render: a re-login or cross-tab switch landing
     * between a `setUser` and that flush read a null mirror, skipped the
     * principal-mismatch reset, and a transient failure of the new token's
     * `/me` then left the previous account's profile beside the new claims
     * (Copilot, PR #2625). Route every profile write through `applyUser`; a
     * bare `setUser` elsewhere reopens that window.
     */
    const userRef = useRef(null);
    const [sessionTooLarge, setSessionTooLarge] = useState(false);
    const applyUser = useCallback((u) => {
        userRef.current = u;
        setUser(u);
        // A profile in hand means `/v1/auth/me` answered, so an earlier 431
        // verdict is stale. Cleared HERE rather than at the three success arms
        // because `applyUser` is already the single funnel every profile write
        // goes through, and a fourth hand-copied line is a fourth chance to drift.
        if (u !== null)
            setSessionTooLarge(false);
    }, []);
    /**
     * The `BOOT_RETRY_GRACE_MS` timer while a boot hold is running, `null` otherwise (R-38).
     *
     * A ref rather than a local, because the hold is armed in the mount effect and ended from the
     * refresh callbacks and the cross-tab handler, which are a different effect's closures.
     */
    const bootHoldRef = useRef(null);
    /**
     * End a boot hold early: cancel the grace timer and drop the spinner.
     *
     * Every way the hold can end goes through here — the retry's success, the retry's refusal, and
     * a `token-refreshed` from another tab (R-38, finding CA2-3). Cancelling matters as much as the
     * `setLoading`: a grace timer left armed would fire `stopSilentRefresh()` fifteen seconds into a
     * healthy session and cancel the proactive refresh the success had just scheduled.
     *
     * Mid-session there is no hold and no timer, so this is one `setLoading(false)` over a `false`.
     */
    const endBootHold = useCallback(() => {
        if (bootHoldRef.current !== null) {
            clearTimeout(bootHoldRef.current);
            bootHoldRef.current = null;
        }
        setLoading(false);
    }, []);
    const claims = useMemo(() => extractClaims(token), [token]);
    /**
     * End a session within `scope`: `ANY_SESSION` for the public `logout`, a token
     * for the session that token belongs to (the three `/me` 401/403 arms and the
     * refresh verdict), `null` for local state while nothing is stored (the hydrate
     * path after `hydrateTokens` cleared a rejected pair).
     *
     * The distinction exists because `revokeServerSession` is awaited, and a login or a
     * refresh can land during that await (Copilot, PR #2625). A `clearTokens()`
     * after the await would then wipe a session this call was never asked to
     * end. So the expected-token form checks currency twice: before the network
     * call (the session may already be gone or replaced) and after it (it may
     * have been replaced while the call was in flight). Skipping the clear is
     * safe: the session that replaced ours has its own recovery path, and the
     * refresh token we revoked server-side was the OLD pair's.
     *
     * The unconditional form stays unconditional on purpose. A user who clicks
     * "Logout" while a silent refresh rotates the token still expects to be
     * logged out, so a token change during the await must not spare that session.
     *
     * The whole read-revoke-recheck-clear sequence runs under the refresh lock.
     * `/v1/auth/logout` revokes the refresh-token FAMILY, so a refresh that
     * rotated the pair while the request was in flight is revoked too; without
     * the lock the recheck saw the rotated pair, skipped the clear, and left a
     * stored session the server had ended (Copilot, PR #2625). Under the lock a
     * running refresh finishes first and the revocation targets the pair it
     * produced; a refresh that starts afterwards finds no tokens and is
     * terminal. A login does not take the lock, and does not need to: it starts
     * a new family, which the revocation does not touch, and the recheck keeps
     * it. Without the Web Locks API the lock is an in-tab queue, so this tab's
     * own refresh still cannot overlap. A refresh in another tab can, and the
     * post-await recheck treats the rotated pair it leaves behind (same
     * principal, different token) as part of the revoked family and clears it.
     */
    const endSession = useCallback(async (scope) => {
        // `null` scopes to the absence of a session: a verdict about "no pair to
        // refresh with" ends only local state, and only while nothing is stored.
        const inScope = (stored) => scope === ANY_SESSION || stored === scope;
        // Outside the lock, and only for the user's own logout with no live bearer. See
        // `mintLogoutBearer`: it takes the same lock, so minting inside this one deadlocks (R-41).
        const mintedBearer = await mintLogoutBearer(scope);
        const ended = await withRefreshLock(async () => {
            const currentToken = getAccessToken();
            if (!inScope(currentToken))
                return false;
            // A live access token is the bearer. Without one the tab minted one above, from the
            // cookie or, on the body arm, from its legacy refresh token, so the server still revokes
            // the family (R-41, R-49). The pre-cookie version read `zion_refresh_token` and
            // `zion_token_expires_at` from `localStorage`; both keys are gone (D2, D11).
            const bearer = currentToken !== null && !isTokenExpired(sessionStore.getExpiresAt())
                ? currentToken
                : mintedBearer;
            if (bearer !== null) {
                try {
                    await revokeServerSession(bearer);
                }
                catch {
                    // Best-effort: a rejection or the timeout skips only the server-side
                    // revocation, which the next refresh with this pair triggers anyway.
                    // The local clear below does not depend on the server's answer.
                }
            }
            // A different stored token after the await is either a rotation of the
            // family the server just revoked (same principal: another tab refreshed
            // while the lock was in-tab only, or the lock was unavailable) or a new
            // login (a different principal, a new family). Clear the first, keep the
            // second. A same-user re-login in another tab inside this window is
            // cleared with the rotation and costs one more login (Copilot, PR #2625).
            const tokenAfterLogout = getAccessToken();
            if (!inScope(tokenAfterLogout)) {
                const samePrincipal = typeof scope === 'string' &&
                    tokenAfterLogout !== null &&
                    extractClaims(tokenAfterLogout).sub === extractClaims(scope).sub;
                if (!samePrincipal)
                    return false;
            }
            clearTokens();
            return true;
        });
        if (!ended)
            return;
        stopSilentRefresh();
        stopSessionSync();
        stopVisibilityRefresh();
        setToken(null);
        applyUser(null);
    }, [applyUser]);
    const logout = useCallback(() => endSession(ANY_SESSION), [endSession]);
    /**
     * See {@link AuthState.login}. `adoptSession(session)` is the one writer: it writes
     * `sessionStore`, takes the mixed-roll branch and posts `token-refreshed` to the other tabs.
     * Everything after it is the pre-cookie behaviour: the principal-mismatch profile drop, the two
     * timers, and the `/v1/auth/me` call with its currency guard and its three-way failure
     * classification.
     */
    const login = useCallback((session) => {
        // ⛔ PARSE BEFORE ADOPTING (R-35). `AuthState.login` is a public entry point and its
        // argument comes off the wire: the shell's `request()` hands back `res.json()` unparsed.
        // Both union members are `.strict()`, so a caller that passes a whole mint body with an
        // extra key is refused here rather than signing a principal in on an unvalidated object.
        // `RecoveryLoginPage`'s strip of `redirectTo` (R-21) and the shell's own S20 control both
        // rest on this parse. The throw is a `SessionResponseError` rather than the raw `ZodError`,
        // because six shell pages render a caught error's `message` into a live region (R-37).
        const parsed = sessionResponseSchema.safeParse(session);
        if (!parsed.success)
            throw new SessionResponseError(parsed.error);
        adoptSession(session);
        setToken(session.accessToken);
        // A login in an already signed-in tab may be a different principal. Drop the cached profile
        // now, so a transient failure of the new token's `/me` cannot leave the previous account's
        // name beside the new claims until a refresh notices the mismatch (Copilot, PR #2625).
        if (userRef.current !== null &&
            userRef.current.id !== extractClaims(session.accessToken).sub) {
            applyUser(null);
        }
        startSilentRefresh();
        startVisibilityRefresh();
        fetchMe(session.accessToken)
            .then((u) => {
            // Login A's profile settling after login B adopted its session would be painted into
            // B's session; same currency guard as `recoverIdentity`.
            if (getAccessToken() !== session.accessToken)
                return;
            applyUser(u);
        })
            .catch((err) => {
            // Only a genuine 401/403 from `/me` logs out; a transient failure (5xx / network) keeps
            // the session and lets a later refresh repopulate. Scoped to the token this call sent,
            // so a 401 for login A that settles after login B has adopted does not end B's session.
            if (isRejectedSession(err)) {
                void endSession(session.accessToken);
                return;
            }
            if (isOversizedRequest(err))
                setSessionTooLarge(true);
        });
    }, [endSession, applyUser]);
    // Hydrate on mount.
    //
    // ⛔ NO `if (!token) return` GUARD. It used to skip hydration when nothing was stored, which was
    // right while the access token lived in `localStorage` and survived a reload. The store is
    // module memory now, so a cold boot always has an empty store and that guard would leave every
    // reload signed out (R-7c). `hydrateTokens` is the one thing that can obtain a token here: it
    // drains the legacy keys and calls the server once with the cookie (D9).
    useEffect(() => {
        let cancelled = false;
        let validToken = null;
        (async () => {
            try {
                validToken = await hydrateTokens();
                if (cancelled)
                    return;
                if (!validToken) {
                    // No token, or a terminal refresh: `hydrateTokens` cleared storage in
                    // the same tick as its verdict. End local state only while nothing is
                    // stored. A login that landed while this waited for the refresh lock
                    // owns the provider now, and its handler has already set the token;
                    // the unconditional `logout()` used to end it (#2680).
                    await endSession(null);
                    setLoading(false);
                    return;
                }
                // A login or a cross-tab replacement can land while `hydrateTokens`
                // is awaiting. Its handler has already set the provider to the new
                // token and started its own `/me`; applying `validToken` here would
                // roll the claims back to the old session and arm the timer from it
                // (Copilot, PR #2625). Let the current session's handler win.
                if (getAccessToken() !== validToken) {
                    setLoading(false);
                    return;
                }
                setToken(validToken);
                // Re-arm the proactive timer from the CURRENT expiry. When the stored
                // token was inside the refresh buffer at mount, `hydrateTokens` refreshed
                // it through `doRefresh` directly, which stores the new pair but neither
                // invokes `onTokenRefreshed` nor calls `scheduleRefresh`. The callbacks
                // effect below ran `startSilentRefresh()` before that refresh landed and
                // saw the OLD near-expired expiry, so `computeRefreshDelay` returned null
                // and armed nothing. Without this line such a tab has no timer until a
                // visibility change or a 401 interceptor, and the recovery below never
                // gets a callback to run from (Copilot, PR #2625).
                startSilentRefresh();
                const u = await fetchMe(validToken);
                // A login, a cross-tab switch or a logout can replace or clear the stored
                // token while `/me` is in flight; the profile belongs to `validToken` only.
                if (!cancelled && getAccessToken() === validToken)
                    applyUser(u);
            }
            catch (err) {
                if (cancelled)
                    return;
                // A 401/403 from /v1/auth/me means the just-validated token is genuinely
                // rejected ⇒ log out. A TransientRefreshError (hydrate) or any other
                // /me failure (5xx / network) is transient ⇒ keep the session; the token
                // stays set and `recoverIdentity` repopulates `user` on the next
                // silent/visibility refresh or on a refresh synced from another tab.
                //
                // ⛔ That last clause used to read "a later proactive refresh /
                // interaction repopulates user" and was FALSE (#2570): `fetchMe` had no
                // call site on the refresh path, so `user` stayed null for the whole
                // session and the identity rendered as "there" / `?` until a reload.
                // The recovery now exists — see `recoverIdentity` below. Do not re-widen
                // this sentence past what that code does: it fires only while `user` is
                // null.
                //
                // ⛔ A 431 is the third class and it is neither of those. `recoverIdentity`
                // WOULD retry it on every refresh, forever, re-sending the same oversized
                // header — so it is recorded instead of retried into silence.
                //
                // ⛔ A `TransientRefreshError` out of `hydrateTokens` is the fourth class, and settling
                // here is what CD-1 caught: the store is empty on a cold boot, so the provider would
                // render the login page after one network blip and nothing would try again (R-30).
                if (err instanceof TransientRefreshError) {
                    // The scheduler cannot arm itself from a null expiry, so arm its backoff directly. One
                    // retry, at `INITIAL_BACKOFF_SEC`.
                    startRefreshRetry();
                    // Hold the spinner until that retry's verdict or the bound, whichever comes first. The
                    // verdict clears it through `endBootHold`; this is the other exit.
                    //
                    // No `cancelled` check inside the callback, because two things already close that
                    // window: the catch returns above when the unmount happened during the await, and the
                    // cleanup clears this timer when it happens after. A third check would be a branch no
                    // test can reach.
                    bootHoldRef.current = setTimeout(() => {
                        bootHoldRef.current = null;
                        // ⛔ STOP THE RETRY, NOT JUST THE SPINNER (R-38). `ProtectedRoute` navigates with
                        // `replace` and `LoginPage` never reads `token`, so a success that lands after this
                        // point signs the user in behind a sign-in form they cannot leave.
                        stopSilentRefresh();
                        setLoading(false);
                    }, BOOT_RETRY_GRACE_MS);
                    return;
                }
                if (isRejectedSession(err)) {
                    // `validToken` is set on this path: `fetchMe` runs after the assignment.
                    await endSession(validToken);
                }
                else if (isOversizedRequest(err)) {
                    setSessionTooLarge(true);
                }
            }
            if (!cancelled)
                setLoading(false);
        })();
        return () => {
            cancelled = true;
            if (bootHoldRef.current !== null) {
                clearTimeout(bootHoldRef.current);
                bootHoldRef.current = null;
            }
        };
    }, []);
    /**
     * The tokens whose recovery `/me` is in flight. `createAuthFetch`'s
     * `refreshAndRetry` invokes `onTokenRefreshed` once per waiter after the ONE
     * coalesced `doRefresh` resolves, so N concurrent 401s hand this provider the
     * same token N times. Without this set each call saw `userRef.current === null`
     * and issued its own `/me`, so a transient outage was amplified N-fold
     * (Copilot, PR #2625). A set rather than one slot: with two tokens in flight
     * the older one settling must not clear the newer one's marker, and the newer
     * one settling must not clear the older one's, or a re-delivery of the
     * survivor issues a duplicate. Each request removes only its own token.
     */
    const recoveriesInFlightRef = useRef(new Set());
    /**
     * ⭐ **THE RECOVERY PATH FOR A TRANSIENT `/me` FAILURE (#2570).**
     *
     * `fetchMe` used to have exactly TWO call sites — `login` and the hydrate
     * effect — and neither ran again. So a 5xx / network / CORS failure at mount
     * left `user` null for the WHOLE SESSION: the hydrate `catch` deliberately
     * keeps the session on a transient failure, and nothing ever tried again.
     * The shell then renders the identity from `user?.fullName === undefined` —
     * "Good evening, there" with a `?` avatar — beside an `orgId`/`orgRoles`
     * pair that renders fine because those come from a LOCAL unverified decode
     * of the token and need no server. That asymmetry is what made it read as a
     * broken session rather than as a failed request, and only a full reload
     * cleared it.
     *
     * ⛔ The hydrate comment ASSERTED this recovery existed ("a later proactive
     * refresh / interaction repopulates user"). It did not. The claim is why the
     * gap survived review, so it is corrected there too — a comment that
     * documents behaviour the code does not have is worse than no comment.
     *
     * Two callers hand a token here: the refresh callback (timer, visibility,
     * and the 401 interceptor) and the cross-tab sync (another tab refreshed).
     * The second matters because a tab whose peers keep refreshing first has its
     * own timer pushed out on every storage event and may never refresh itself.
     *
     * ⛔ CONDITIONAL, via `userRef` and not `user` — see the ref's docblock.
     * Unconditional would add a `/me` per refresh interval to every session
     * forever to fix a state that is rare. The condition is "no profile for THIS
     * principal": the cached profile's `id` is compared with the token's `sub`,
     * which the DP mints as `user.id`. A null check alone kept Alice's name
     * beside Bob's claims after a cross-tab account switch, and every later
     * refresh skipped `/me` because `user` was populated (Copilot, PR #2625).
     * On a mismatch the stale profile is dropped before the refetch, so the UI
     * shows no identity rather than the wrong one while `/me` is in flight.
     *
     * ⛔ The failure classification is IDENTICAL to the other two call sites and
     * must stay that way — which is now a mechanism rather than a promise: all
     * three read `isRejectedSession` and `isOversizedRequest`. 401/403 is
     * terminal (the token is genuinely rejected ⇒ end the session); 431 keeps
     * the session and sets `sessionTooLarge`, because retrying it re-sends the
     * same oversized header and a login mints a larger token; anything else is
     * transient (keep the session, leave `user` null, and the next refresh
     * retries — so the recovery is idempotent and self-healing). Logging out on
     * a 5xx here would turn a backend blip into a forced re-auth for every open
     * session.
     *
     * ⛔ BOTH ARMS ARE GATED ON THE TOKEN STILL BEING CURRENT, AND THE TWO RACES
     * THEY CLOSE ARE DIFFERENT ONES (Copilot, PR #2625). `/me` is in flight
     * across an `await`, and the session can be torn down or REPLACED underneath
     * it.
     *
     *  - **Success arm.** A logout mid-flight would otherwise resurrect a
     *    profile into a logged-out session, and an account SWITCH mid-flight
     *    would paint the PREVIOUS account's name and avatar onto the new one.
     *    Gated inline on `getAccessToken() === token`.
     *  - **Failure arm.** A stale 401 belonging to the OLD token would end a
     *    session that a different login had just established. Gated inside
     *    `endSession(token)`, which re-checks currency on both sides of its own
     *    awaited `revokeServerSession`, because a login can also land DURING that call.
     *
     * `getAccessToken()` is the discriminator and it is LIVE, not vacuous:
     * `doRefresh` calls `adoptSession` BEFORE returning, `runScheduledRefresh`
     * invokes the callback after, and `startSessionSync`'s handler writes
     * `sessionStore` before it calls sync — so at entry `getAccessToken() === token`
     * holds, and it stops holding exactly when `clearTokens()` (logout) or
     * another `sessionStore.set` (login / a later refresh) has run.
     *
     * ⚠️ A newer refresh landing first also fails the check, and that is
     * correct: `user` is still null, so that refresh runs its OWN recovery.
     */
    const recoverIdentity = useCallback((token) => {
        const cached = userRef.current;
        if (cached !== null && cached.id === extractClaims(token).sub)
            return;
        // Drop a mismatched profile BEFORE the in-flight check. Switching to B
        // (which populates B) and back to A while A's first `/me` is still open
        // must not leave B's profile beside A's claims because A already has a
        // marker (Copilot, PR #2625).
        if (cached !== null)
            applyUser(null);
        if (recoveriesInFlightRef.current.has(token))
            return;
        recoveriesInFlightRef.current.add(token);
        fetchMe(token)
            .then((u) => {
            if (getAccessToken() !== token)
                return;
            applyUser(u);
        })
            .catch((err) => {
            if (isRejectedSession(err)) {
                void endSession(token);
                return;
            }
            if (isOversizedRequest(err))
                setSessionTooLarge(true);
        })
            .finally(() => {
            recoveriesInFlightRef.current.delete(token);
        });
    }, [endSession, applyUser]);
    /**
     * See {@link AuthState.refreshSession}. It awaits `refreshAccessToken()`, then sets the token,
     * re-arms the proactive timer and runs `recoverIdentity(newToken)` so the new claims and the
     * profile agree. A `TerminalAuthError` ends the session it judged; every other failure is
     * transient and is swallowed, so the promise settles in all three cases and never rejects.
     */
    const refreshSession = useCallback(async () => {
        try {
            const newToken = await refreshAccessToken();
            setToken(newToken);
            // Re-arm from the NEW expiry: `doRefresh` writes the store but invokes no callback and
            // schedules nothing, so without this the tab keeps the timer the old expiry armed.
            startSilentRefresh();
            recoverIdentity(newToken);
        }
        catch (err) {
            if (err instanceof TerminalAuthError) {
                // The verdict names the pair it judged; end that session and no other (#2680).
                await endSession(err.token);
                return;
            }
            // Transient: the session is untouched and the scheduler retries under its own backoff.
            // The one caller is best-effort and reloads either way, so nothing is surfaced.
        }
    }, [endSession, recoverIdentity]);
    // Wire up refresh callbacks
    useEffect(() => {
        setRefreshCallbacks((newToken) => {
            setToken(newToken);
            // A retry after a transient boot failure lands here, and it is the verdict the hold is
            // waiting for (R-38). Mid-session there is no hold and this only re-sets a `false`.
            endBootHold();
            startSilentRefresh();
            recoverIdentity(newToken);
        }, 
        // The refresh verdict names the pair it judged; end that session and no
        // other. A login stored between the verdict and the lock survives (#2680).
        // The hold ends for the same reason the success arm ends it: a refusal is the other verdict
        // it waits for, and it settles the tab signed out at once rather than at the bound (R-38).
        (judgedToken) => {
            endBootHold();
            void endSession(judgedToken);
        });
        startSilentRefresh();
        startVisibilityRefresh();
        // `startSessionSync` replaces `startCrossTabSync`: the `storage` event cannot carry a session
        // any more, because nothing this package writes to Web Storage changes when one does (D10).
        // The handler below does not move.
        startSessionSync((syncedToken) => {
            if (syncedToken) {
                setToken(syncedToken);
                // A token from another tab is the third way the boot hold ends (R-38, finding CA2-3).
                // This arm calls no refresh callback, and `startSessionSync`'s own `scheduleRefresh()`
                // cancels the pending retry, so without this the spinner ran the full bound over a live
                // session.
                endBootHold();
                recoverIdentity(syncedToken);
            }
            else {
                setToken(null);
                applyUser(null);
            }
        });
        return () => {
            stopSilentRefresh();
            stopSessionSync();
            stopVisibilityRefresh();
        };
    }, [recoverIdentity, applyUser, endSession, endBootHold]);
    const value = useMemo(() => ({
        token,
        user,
        loading,
        orgId: claims.orgId,
        orgRoles: claims.orgRoles,
        orgPermissions: claims.orgPermissions,
        siteRoles: claims.siteRoles,
        groups: claims.groups,
        ring: claims.ring,
        customerChannelId: claims.customerChannelId,
        customerChannelName: claims.customerChannelName,
        login,
        refreshSession,
        logout,
        sessionTooLarge,
    }), [token, user, loading, claims, login, refreshSession, logout, sessionTooLarge]);
    // Expose the principal's permission snapshot to the SDK's shared
    // `useHasPermission()` hook. The DP issues NO site-level permissions and NO
    // per-resource grants in this slice, so `sitePermissions` and `resourceGrants`
    // are intentionally hardcoded empty: `useHasPermission(key)` checks org/site
    // ROLES + orgPermissions (with `*`/prefix wildcards) but NEVER matches a
    // site-permission string or a `(permission, resourceId)` resource grant.
    // This limitation is documented in `docs/sdk/bot/api-react-auth.md`; it lifts
    // automatically once the DP populates those claims and AuthProvider wires them
    // here from the verified token (no hook/API change needed — the slots fill).
    const permissionSnapshot = useMemo(() => ({
        siteRoles: claims.siteRoles,
        sitePermissions: [],
        orgRoles: claims.orgRoles,
        orgPermissions: claims.orgPermissions,
        resourceGrants: [],
        appPermissionKeys: claims.appPermissionKeys,
    }), [claims.siteRoles, claims.orgRoles, claims.orgPermissions, claims.appPermissionKeys]);
    return (_jsx(AuthContext, { value: value, children: _jsx(PermissionProvider, { value: permissionSnapshot, children: children }) }));
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
//# sourceMappingURL=auth-context.js.map